import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { UseFormReturn } from 'react-hook-form';
import ScheduledReportsList from './ScheduledReportsList';
import { ScheduledReport, FilterFormValues, ScheduledReportsTabId } from '../types';
import { scheduledReportsTabsSx, tabPanelBoxSx, tabsContainerSx } from '../styles';
import { createTabsConfig, filterReportsByTab } from '../utils/ViewScheduleReportUtil';
import { useUsers } from '../../contexts/userContext';

interface TabPanelProps {
  children: React.ReactNode;
  index: ScheduledReportsTabId;
  value: ScheduledReportsTabId;
}

interface ScheduledReportsContentProps {
  reports: ScheduledReport[];
  isAdmin: boolean;
  formContext: UseFormReturn<FilterFormValues>;
  search: string;
  onSearchChange: (value: string) => void;
}

function TabPanel({ children, value, index }: Readonly<TabPanelProps>) {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`scheduled-reports-tabpanel-${index}`}
      aria-labelledby={`scheduled-reports-tab-${index}`}
      data-testid={`scheduled-reports-tabpanel-${index}`}
    >
      {value === index && <Box sx={tabPanelBoxSx}>{children}</Box>}
    </div>
  );
}

function ScheduledReportsTabs({
  reports,
  isAdmin,
  formContext,
  search = '',
  onSearchChange,
}: Readonly<ScheduledReportsContentProps>) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ScheduledReportsTabId>(
    isAdmin ? ScheduledReportsTabId.All : ScheduledReportsTabId.ScheduledByYou
  );
  const user = useUsers();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ScheduledReportsTabId) => {
      setActiveTab(newValue);
      onSearchChange(''); // Clear search when tab changes
    },
    [onSearchChange]
  );

  // Get tabs configuration and filter reports based on user role
  const { tabs, tabFilteredReports } = useMemo(() => {
    const tabConfig = createTabsConfig(isAdmin, t);
    const filtered = filterReportsByTab(reports, user, activeTab);

    return { tabs: tabConfig, tabFilteredReports: filtered };
  }, [isAdmin, reports, user, activeTab, t]);

  return (
    <Grid container direction='column' sx={tabsContainerSx}>
      <Grid item xs={12}>
        <Box sx={scheduledReportsTabsSx}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label='scheduled reports tabs'
            data-testid='scheduled-reports-tabs'
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                id={`scheduled-reports-tab-${tab.id}`}
                aria-controls={`scheduled-reports-tabpanel-${tab.id}`}
                data-testid={`tab-${tab.id}`}
              />
            ))}
          </Tabs>
        </Box>
      </Grid>

      {tabs.map((tab) => (
        <Grid item xs={12} key={tab.id}>
          <TabPanel value={activeTab} index={tab.id}>
            <ScheduledReportsList
              reports={tabFilteredReports}
              isLoading={false}
              formContext={formContext}
              search={search}
              onSearchChange={onSearchChange}
              showFilters={!!formContext}
              currentTabId={activeTab}
              allReports={reports}
            />
          </TabPanel>
        </Grid>
      ))}
    </Grid>
  );
}

export default ScheduledReportsTabs;
